import type { useGlobal } from "@/context/global"
import { ServerConnection } from "@/context/server"
import { tabKey, type SessionTab, type useTabs } from "@/context/tabs"

export function createCoworkChatCloser(deps: {
  global: Pick<ReturnType<typeof useGlobal>, "servers" | "ensureServerCtx">
  tabs: Pick<ReturnType<typeof useTabs>, "store" | "closeTab">
  beforeClose: (tab: SessionTab) => void
  isCurrent: () => boolean
}) {
  const closing = new Set<string>()
  return async (tab: SessionTab) => {
    const key = tabKey(tab)
    if (closing.has(key) || !deps.isCurrent()) return
    closing.add(key)
    try {
      const connection = deps.global.servers.list().find((item) => ServerConnection.key(item) === tab.server)
      // A stale local tab must still be closable. Never substitute another
      // server (or delete the saved conversation) when its connection is absent.
      if (connection) {
        const context = deps.global.ensureServerCtx(connection)
        if (context.sync.session.data.session_working(tab.sessionId)) {
          await context.sdk.api.session.interrupt({ sessionID: tab.sessionId })
        }
      }
      if (!deps.isCurrent()) return
      // Other tabs can close or move while the cancellation request is in flight.
      const index = deps.tabs.store.findIndex((item) => tabKey(item) === key)
      if (index < 0) return
      deps.beforeClose(tab)
      // The dock owns navigation; the shared fallback targets the outer router.
      deps.tabs.closeTab(index, () => {})
    } finally {
      closing.delete(key)
    }
  }
}
